<template>
  <div>
    <label :for="id" class="block text-xs font-medium text-muted mb-1.5">{{ label }}</label>
    <div class="relative">
      <input
        :id="id"
        ref="inputRef"
        v-bind="$attrs"
        :type="inputType"
        :value="modelValue"
        :aria-invalid="error ? 'true' : undefined"
        :aria-describedby="error ? errorId : undefined"
        class="w-full px-3 py-2 rounded-[10px] border bg-surface text-sm text-text placeholder:text-muted/60 outline-none transition-colors focus:ring-2"
        :class="[
          error
            ? 'border-error focus:border-error focus:ring-error/15'
            : 'border-border focus:border-primary focus:ring-primary/15',
          type === 'password' ? 'pr-10' : '',
        ]"
        @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
        @blur="$emit('blur')"
      />
      <button
        v-if="type === 'password'"
        type="button"
        tabindex="-1"
        class="absolute inset-y-0 right-3 flex items-center text-muted/60 hover:text-text transition-colors"
        :aria-label="revealed ? 'Hide password' : 'Show password'"
        @click="revealed = !revealed"
      >
        <Eye v-if="!revealed" :size="18" />
        <EyeOff v-else :size="18" />
      </button>
    </div>
    <div
      v-if="reserveErrorSpace || error || $slots.meta"
      class="mt-1 flex items-start justify-between gap-2 text-xs"
      :class="reserveErrorSpace ? 'min-h-4' : ''"
    >
      <p :id="errorId" class="text-error">{{ error }}</p>
      <slot name="meta" />
    </div>
  </div>
</template>

<script setup lang="ts">
// One labelled input with its error line. Non-prop attributes (autocomplete,
// inputmode, autofocus, maxlength, placeholder, disabled) land on the <input>.
import { computed, ref, useId } from 'vue'
import { Eye, EyeOff } from 'lucide-vue-next'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    modelValue: string
    label: string
    type?: 'text' | 'email' | 'password'
    error?: string
    /** Keep a one-line slot under the input so validation never shifts the layout. */
    reserveErrorSpace?: boolean
  }>(),
  { type: 'text', error: '', reserveErrorSpace: true },
)

defineEmits<{
  'update:modelValue': [value: string]
  blur: []
}>()

const id = useId()
const errorId = `${id}-error`
const inputRef = ref<HTMLInputElement | null>(null)
const revealed = ref(false)

const inputType = computed(() => (props.type === 'password' && revealed.value ? 'text' : props.type))

defineExpose({
  focus: () => inputRef.value?.focus(),
  select: () => inputRef.value?.select(),
})
</script>
